// Normalises a dashboard pathname into a stable, low-cardinality `screen` name +
// id context, so PostHog can aggregate passive/consumption usage across our
// multi-tenant dynamic routes (/[orgSlug]/[projectSlug]/...). Used by the
// `before_send` enrichment hook (see enrich.ts). Keep the Screen union FROZEN —
// adding a screen is a deliberate edit, not an accident.

export type Screen =
  | 'fleetMap'
  | 'deviceList'
  | 'deviceDetail'
  | 'deviceCreate'
  | 'analytics'
  | 'geofenceList'
  | 'geofenceMap'
  | 'alertInstances'
  | 'alertRules'
  | 'alertCreate'
  | 'alertEdit'
  | 'bookingList'
  | 'bookingDetail'
  | 'bookingCreate'
  | 'bookingImport'
  | 'settings'
  | 'memberships'
  | 'webhooks'
  | 'sharedAccessList'
  | 'sharedAccessDetail'
  | 'admin'
  | 'orgRoot'
  | 'unknown';

type ScreenResult = { screen: Screen; context: Record<string, string> };

// Top-level routes that are NOT tenant-scoped (no /[orgSlug]/[projectSlug] prefix).
const TOP_LEVEL = ['admin', 'sign-in', 'sign-up', 'onboarding', 'accept-invitation', 'external'];

export const screenFromPathname = (pathname: string): ScreenResult => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return { screen: 'orgRoot', context: {} };

  if (TOP_LEVEL.includes(segments[0])) {
    if (segments[0] === 'admin')
      return { screen: 'admin', context: { section: segments[1] ?? 'home' } };
    // Pre-auth / external routes — not part of in-app passive usage. Deliberately
    // omit rawPath: invitation/external links can carry tokens/emails in the path.
    return { screen: 'unknown', context: {} };
  }

  // Tenant route: [orgSlug, projectSlug, ...rest]. `/[orgSlug]` alone is the
  // transient org-root redirect.
  if (segments.length <= 1) return { screen: 'orgRoot', context: {} };

  const [section, sub, subSub] = segments.slice(2);
  if (!section) return { screen: 'fleetMap', context: {} };

  switch (section) {
    case 'devices':
      if (!sub) return { screen: 'deviceList', context: {} };
      if (sub === 'create') return { screen: 'deviceCreate', context: {} };
      return { screen: 'deviceDetail', context: { deviceId: sub } };

    case 'analytics':
      return { screen: 'analytics', context: {} };

    case 'geofences':
      return sub === 'map'
        ? { screen: 'geofenceMap', context: {} }
        : { screen: 'geofenceList', context: {} };

    case 'alerts':
      if (!sub) return { screen: 'alertInstances', context: {} };
      if (sub === 'create') return { screen: 'alertCreate', context: {} };
      if (sub === 'configure') {
        return subSub
          ? { screen: 'alertEdit', context: { alertId: subSub } }
          : { screen: 'alertRules', context: {} };
      }
      return { screen: 'alertInstances', context: {} };

    case 'bookings':
      if (!sub) return { screen: 'bookingList', context: {} };
      if (sub === 'create') return { screen: 'bookingCreate', context: {} };
      if (sub === 'import') return { screen: 'bookingImport', context: {} };
      return { screen: 'bookingDetail', context: { bookingId: sub } };

    case 'settings':
      switch (sub) {
        case 'members':
          return { screen: 'memberships', context: { section: 'project' } };
        case 'organization-members':
          return { screen: 'memberships', context: { section: 'organization' } };
        case 'webhooks':
          return { screen: 'webhooks', context: {} };
        case 'shared-links':
          return subSub
            ? { screen: 'sharedAccessDetail', context: { linkId: subSub } }
            : { screen: 'sharedAccessList', context: {} };
      }
      return { screen: 'settings', context: { section: sub ?? 'home' } };

    default:
      return { screen: 'unknown', context: { rawPath: pathname } };
  }
};
